import React from "react";
import PropTypes from "prop-types";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment-timezone";
import LessonWrapper from "./LessonWrapper";
import {
  hasPartialCancellation,
  isAttendanceChecked,
  isLessonCancelled,
} from "./lessonAttendanceStatus";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/sass/styles.scss";

const localizer = momentLocalizer(moment.tz.setDefault("Europe/Warsaw"));
const ATTENDED_EVENT_BACKGROUND = "#0c4946";
const CANCELLED_EVENT_BACKGROUND = "#6c757d";
const PARTIALLY_CANCELLED_EVENT_BACKGROUND = "#856404";

function DashboardCalendarEvent({ event }) {
  const { calendarContext } = event;

  return (
    <LessonWrapper
      event={event}
      display="calendar-event"
      students={calendarContext.students}
      teachers={calendarContext.teachers}
      onSubmit={calendarContext.onSubmit}
      deleteLessonRegister={calendarContext.deleteLessonRegister}
      role={calendarContext.userRole}
      userTeacherId={calendarContext.userTeacherId}
    />
  );
}

DashboardCalendarEvent.propTypes = {
  event: PropTypes.shape({
    scheduledLesson: PropTypes.object.isRequired,
    calendarContext: PropTypes.shape({
      students: PropTypes.array.isRequired,
      teachers: PropTypes.array.isRequired,
      onSubmit: PropTypes.func.isRequired,
      deleteLessonRegister: PropTypes.func.isRequired,
      userRole: PropTypes.string,
      userTeacherId: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

function DashboardCalendar({
  scheduledClasses,
  students,
  teachers,
  onSubmit,
  deleteLessonRegister,
  onVisibleDateChange,
  userRole,
  userTeacherId,
}) {
  const events = scheduledClasses.flatMap(scheduledClass => (
    scheduledClass.scheduledLessons.map((scheduledLesson) => {
      const duration = parseInt(scheduledLesson.defaultLessonDetails.classTypeId.slice(1), 10);
      const start = moment(
        scheduledLesson.lessonRegister
          ? `${scheduledLesson.lessonRegister.lessonDetails.date} ${scheduledLesson.lessonRegister.lessonDetails.startTime}`
          : `${scheduledLesson.defaultLessonDetails.date} ${scheduledLesson.defaultLessonDetails.startTime}`,
        "YYYY-MM-DD HH:mm",
      ).toDate();
      const end = moment(start).add(duration, "minutes").toDate();

      return {
        id: scheduledLesson.id,
        title: `${scheduledLesson.defaultLessonDetails.classTypeId}`,
        start,
        end,
        scheduledLesson,
        calendarContext: {
          students,
          teachers,
          onSubmit,
          deleteLessonRegister,
          userRole,
          userTeacherId,
        },
      };
    })
  ));

  const handleResize = () => {
    if (window.innerWidth < 768) {
      return "day";
    }

    return "week";
  };

  const currentRoundedTime = () => {
    const currentTime = new Date();
    const minutes = currentTime.getMinutes();
    const roundedMinutes = Math.round(minutes / 30) * 30;
    currentTime.setMinutes(roundedMinutes);
    return currentTime;
  };

  const dayStart = moment().hours(8).minutes(0).toDate();
  const dayEnd = moment().hours(20).minutes(0).toDate();
  const eventPropGetter = ({ scheduledLesson }) => {
    if (isLessonCancelled(scheduledLesson)) {
      return {
        className: "dashboard-cancelled-event",
        style: { backgroundColor: CANCELLED_EVENT_BACKGROUND },
      };
    }

    if (hasPartialCancellation(scheduledLesson)) {
      return {
        className: "dashboard-partially-cancelled-event",
        style: { backgroundColor: PARTIALLY_CANCELLED_EVENT_BACKGROUND },
      };
    }

    return isAttendanceChecked(scheduledLesson)
      ? {
        className: "dashboard-attended-event",
        style: { backgroundColor: ATTENDED_EVENT_BACKGROUND },
      }
      : {};
  };

  return (
    <div className="my-calendar">
      <Calendar
        localizer={localizer}
        events={events}
        views={["month", "week", "day"]}
        onNavigate={onVisibleDateChange}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 750 }}
        min={dayStart}
        max={dayEnd}
        step={20}
        timeslots={3}
        defaultView={handleResize()}
        scrollToTime={currentRoundedTime()}
        eventPropGetter={eventPropGetter}
        components={{
          event: DashboardCalendarEvent,
        }}
      />
    </div>
  );
}

DashboardCalendar.propTypes = {
  scheduledClasses: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  teachers: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  deleteLessonRegister: PropTypes.func.isRequired,
  onVisibleDateChange: PropTypes.func,
  userRole: PropTypes.string,
  userTeacherId: PropTypes.string,
};

DashboardCalendar.defaultProps = {
  onVisibleDateChange: () => {},
  userRole: "",
  userTeacherId: "",
};

export default DashboardCalendar;
